import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { COGNITO_CONFIG, getRedirectUri } from "../config/cognitoConfig";

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.userPoolId,
  ClientId: COGNITO_CONFIG.clientId,
});

// ─── Unified token storage ──────────────────────────────────────────────────
// Regardless of whether a user authenticated via SRP (email/password) or the
// OAuth code flow (Apple/Google), the resulting tokens are normalized into
// this same storage shape. This lets session-checking and token-refresh
// logic stay single-path instead of branching on how the user logged in —
// Cognito's /oauth2/token refresh_token grant works the same way for tokens
// issued by either flow, since they're both issued by the same App Client.

const STORAGE_KEY = "nephrotrax_auth_tokens";

function saveTokens({ idToken, accessToken, refreshToken, expiresIn }) {
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ idToken, accessToken, refreshToken, expiresAt })
  );
}

function getStoredTokens() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearTokens() {
  localStorage.removeItem(STORAGE_KEY);
}

// Minimal, dependency-free JWT payload decoder — we only need to read
// claims (email, name, sub, cognito:groups), never verify the signature
// client-side (the backend does real signature verification on every
// API call; this is purely for populating the UI's `user` object).
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// ─── PKCE helpers (required for the OAuth code flow — Apple/Google) ───────────

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateRandomString(length = 64) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer).slice(0, length);
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

// ─── OAuth code flow (Apple / Google federation) ───────────────────────────

/**
 * Redirects the browser to Cognito's Hosted UI /oauth2/authorize endpoint
 * for the given identity provider ("SignInWithApple" or "Google"). Cognito
 * then redirects to that provider's own sign-in screen — Apple's/Google's
 * actual UI, not anything we render ourselves.
 */
export async function redirectToFederatedSignIn(identityProvider) {
  const codeVerifier = generateRandomString(64);
  const state = generateRandomString(32);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem("pkce_code_verifier", codeVerifier);
  sessionStorage.setItem("oauth_state", state);

  const params = new URLSearchParams({
    client_id: COGNITO_CONFIG.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: getRedirectUri(),
    identity_provider: identityProvider,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  window.location.href = `https://${COGNITO_CONFIG.hostedUiDomain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Called from the /auth/callback page once Cognito redirects back with a
 * `code` query param. Exchanges it for tokens and saves them via the
 * unified storage above.
 */
export async function handleOAuthCallback(code, returnedState) {
  const expectedState = sessionStorage.getItem("oauth_state");
  const codeVerifier = sessionStorage.getItem("pkce_code_verifier");

  if (!expectedState || returnedState !== expectedState) {
    throw new Error("OAuth state mismatch — possible CSRF attempt, aborting sign-in.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: COGNITO_CONFIG.clientId,
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: codeVerifier,
  });

  const response = await fetch(`https://${COGNITO_CONFIG.hostedUiDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  sessionStorage.removeItem("pkce_code_verifier");
  sessionStorage.removeItem("oauth_state");

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to exchange authorization code: ${errText}`);
  }

  const tokens = await response.json();
  saveTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
  });

  return tokens;
}

async function refreshWithRefreshToken(refreshToken) {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: COGNITO_CONFIG.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(`https://${COGNITO_CONFIG.hostedUiDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error("Failed to refresh session — user needs to sign in again.");
  }

  const tokens = await response.json();
  // Note: Cognito's refresh grant does not return a new refresh_token —
  // the original one keeps working until it expires (30 days, per our
  // App Client config), so we keep the existing one.
  saveTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken,
    expiresIn: tokens.expires_in,
  });

  return tokens;
}

// ─── Email / password (SRP flow via amazon-cognito-identity-js) ───────────

export function signUp(email, password, name) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: "email", Value: email }),
      new CognitoUserAttribute({ Name: "name", Value: name }),
    ];

    userPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export function resendConfirmationCode(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        saveTokens({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          expiresIn: session.getIdToken().getExpiration() - Math.floor(Date.now() / 1000),
        });
        resolve(session);
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        reject(
          new Error(
            "This account requires a password reset before signing in. Use 'Forgot password' to set a new one."
          )
        );
      },
    });
  });
}

export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export function confirmForgotPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export function signOut() {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
  clearTokens();
}

// ─── Unified session access (used by the useAuth0-compatible hook) ────────

/**
 * Returns { idToken, accessToken, claims } for the current session, silently
 * refreshing via the stored refresh_token if the access token has expired.
 * Returns null if there's no valid session (equivalent to Auth0's
 * "not authenticated").
 */
export async function getCurrentSession() {
  let tokens = getStoredTokens();
  if (!tokens) return null;

  const isExpired = Date.now() >= tokens.expiresAt - 30_000; // refresh 30s early
  if (isExpired) {
    try {
      await refreshWithRefreshToken(tokens.refreshToken);
      tokens = getStoredTokens();
    } catch {
      clearTokens();
      return null;
    }
  }

  const claims = decodeJwtPayload(tokens.idToken);
  if (!claims) return null;

  return { idToken: tokens.idToken, accessToken: tokens.accessToken, claims };
}
